import {
    PlannedDeclarationResponse,
    UnforeseenAbsence,
} from 'api/generated/models';
import useLocale from 'locale/useLocale';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { NewDeclarationFormValues } from 'pages/SubmissionFormWizard/SubmissionFormWizard';
import { Button, Radio, Tooltip } from '@appkit4/react-components';
import PlannedDatesRow from 'pages/SubmissionFormWizard/SubmissionStepThree/PlannedSubmissionStepThree/PlannedDatesRow/PlannedDatesRow';
import { useUserDeclarations } from 'contexts/UserDeclarationsProvider/UserDeclarationsProvider';
import { useAbsenceReasons } from 'contexts/AbsenceReasonsProvider/AbsenceReasonsProvider';
import { Loading } from '@appkit4/react-components/loading';
import hasErrors from 'helpers/hasErrors';
import useFocusOnRender from 'helpers/useFocusOnRender';
import { renderFurtherInformation } from 'helpers/reasonInformation/ConfigureFurtherInfoString';
import { Input } from '@appkit4/react-components';

export type PlannedSubmissionStepThreeProps = {
    submissionIdForEditFlow?: string;
    previousPlannedSubmissionData: PlannedDeclarationResponse | undefined;
    previousPlannedSubmissionDataIsPending: boolean;
};

const PlannedSubmissionStepThree = ({
    submissionIdForEditFlow,
    previousPlannedSubmissionData,
    previousPlannedSubmissionDataIsPending,
}: PlannedSubmissionStepThreeProps) => {
    const { l } = useLocale();
    const { values, setFieldValue, errors } =
        useFormikContext<NewDeclarationFormValues>();
    const { plannedAbsenceReasons } = useAbsenceReasons();
    const inputRef = useRef<HTMLInputElement>(null);
    const approverNameRef = useRef<HTMLInputElement>(null);
    const plannedBusinessReason =
        'Temporary business requirements (with partner or managing director agreement)';

    useFocusOnRender(inputRef);

    const today = new Date();
    const todayTimestamp = today.getTime();

    const { data: userDeclarationsData } = useUserDeclarations();

    const addNewRow = () => {
        if (
            values.dateRanges?.length &&
            values.dateRanges.length < 5 &&
            !values.dateRanges.some(
                (range) => range.startDate == null || range.endDate == null
            )
        ) {
            const newDateRange = {
                declarationId: undefined,
                startDate: undefined,
                endDate: undefined,
            };
            setFieldValue('dateRanges', [...values.dateRanges, newDateRange]);
        }
    };

    const collectDisabledRanges = useCallback(() => {
        const disabledRanges: [Date, Date][] = [];

        userDeclarationsData?.plannedSubmissions?.forEach((submission) => {
            submission &&
                submission?.dateRanges?.forEach((range) => {
                    disabledRanges.push([
                        new Date(range.startDate),
                        new Date(range.endDate),
                    ]);
                });
        });

        // disabled dates from unforeseen - create ranges from single dates
        userDeclarationsData?.unforeseenSubmissions?.forEach((submission) => {
            submission &&
                submission?.unforeseenAbsences?.forEach(
                    (unforeseenAbsence: UnforeseenAbsence) => {
                        disabledRanges.push([
                            new Date(unforeseenAbsence.startDate),
                            new Date(unforeseenAbsence.startDate),
                        ]);
                    }
                );
        });

        // disable ranges from other calendar pickers on the same page
        values?.dateRanges?.forEach((range) => {
            if (range.startDate && range.endDate) {
                const rangeArray: [Date, Date] = [
                    new Date(range.startDate),
                    new Date(range.endDate),
                ];
                disabledRanges.push(rangeArray);
            }
        });

        const uniqueDisabledRanges = disabledRanges.filter(
            (range, index, self) =>
                index ===
                self.findIndex(
                    (r) =>
                        r[0].getTime() === range[0].getTime() &&
                        r[1].getTime() === range[1].getTime()
                )
        );

        return uniqueDisabledRanges;
    }, [todayTimestamp, userDeclarationsData, values, values.dateRanges]);

    const [disabledRanges, setDisabledRanges] = useState<[Date, Date][]>(
        collectDisabledRanges()
    );

    useEffect(() => {
        setDisabledRanges(collectDisabledRanges());
        // IMPORTANT: do not add collectDisabledRanges to this dependency array
    }, [values.dateRanges]);

    useEffect(() => {
        if (submissionIdForEditFlow && previousPlannedSubmissionData) {
            const updatedDateRanges =
                previousPlannedSubmissionData?.declarationInformation?.map(
                    (info) => ({
                        startDate: new Date(info.dateRange.startDate),
                        endDate: new Date(info.dateRange.endDate),
                        declarationId: info.declarationId || undefined,
                    })
                );
            setFieldValue('dateRanges', updatedDateRanges);
        }
    }, [submissionIdForEditFlow, previousPlannedSubmissionData, setFieldValue]);

    const renderDateRanges = useCallback(() => {
        return (
            <>
                {/*EDIT FLOW - previous planned submission data*/}
                {submissionIdForEditFlow
                    ? previousPlannedSubmissionData &&
                      previousPlannedSubmissionData?.declarationInformation?.map(
                          (PlannedDeclarationInfoObject, index) => {
                              if (
                                  !values.declarationIdsToDelete?.includes(
                                      PlannedDeclarationInfoObject.declarationId
                                  )
                              ) {
                                  return (
                                      <PlannedDatesRow
                                          key={
                                              PlannedDeclarationInfoObject.declarationId
                                          }
                                          index={
                                              PlannedDeclarationInfoObject.declarationId
                                          }
                                          dateRange={[
                                              new Date(
                                                  PlannedDeclarationInfoObject.dateRange.startDate
                                              ),
                                              new Date(
                                                  PlannedDeclarationInfoObject.dateRange.endDate
                                              ),
                                          ]}
                                          disabledRanges={disabledRanges}
                                          declarationId={
                                              PlannedDeclarationInfoObject.declarationId
                                          }
                                      />
                                  );
                              }
                          }
                      )
                    : null}

                {/*EDIT FLOW - new date ranges have no declarationId*/}
                {submissionIdForEditFlow
                    ? values?.dateRanges?.map((dateRange, index) => {
                          if (dateRange.declarationId === undefined) {
                              return (
                                  <PlannedDatesRow
                                      key={index}
                                      index={index.toString()}
                                      dateRange={[
                                          dateRange.startDate,
                                          dateRange.endDate,
                                      ]}
                                      disabledRanges={disabledRanges}
                                      declarationId={undefined}
                                  />
                              );
                          }
                      })
                    : null}

                {/*CREATE FLOW*/}
                {!submissionIdForEditFlow
                    ? Array.isArray(values.dateRanges) &&
                      values.dateRanges.map((dateRange, index) => {
                          return (
                              <PlannedDatesRow
                                  key={dateRange.declarationId || index}
                                  index={
                                      dateRange.declarationId ||
                                      index.toString()
                                  }
                                  dateRange={[
                                      dateRange.startDate,
                                      dateRange.endDate,
                                  ]}
                                  disabledRanges={disabledRanges}
                              />
                          );
                      })
                    : null}
            </>
        );
    }, [
        submissionIdForEditFlow,
        previousPlannedSubmissionData,
        values.dateRanges,
        disabledRanges,
        setFieldValue,
        userDeclarationsData,
    ]);

    return (
        <>
            {previousPlannedSubmissionDataIsPending ? (
                <Loading loadingType="circular" indeterminate compact={false} />
            ) : (
                <>
                    <p>
                        {submissionIdForEditFlow
                            ? l('_editPleaseEnterDatePlannedCircumstance')
                            : l('_pleaseEnterDatePlannedCircumstance')}
                    </p>
                    {values?.declarationType ? (
                        <h2 className="ap-typography-heading-s">
                            {submissionIdForEditFlow
                                ? l('_editPlannedCircumstance')
                                : values.declarationType}
                        </h2>
                    ) : null}
                    <div className="change-declarationType-main d-flex align-items-center">
                        <Radio
                            inputRef={inputRef}
                            value={values.reasonForAbsence}
                            checked
                        >
                            {values.reasonForAbsence}
                        </Radio>
                    </div>

                    {values.reasonForAbsence === plannedBusinessReason && (
                        <div className="mt-2 textfieldcontainer">
                            <label>{l('_textboxfield')}</label>
                            <Input
                                type="text"
                                value={values.approverName ?? ''}
                                onChange={(value: any) =>
                                    setFieldValue('approverName', value)
                                }
                                inputRef={approverNameRef}
                            />
                        </div>
                    )}

                    {plannedAbsenceReasons
                        ?.filter(
                            (plannedAbsenceReason) =>
                                plannedAbsenceReason.furtherInformation &&
                                values.reasonForAbsence ===
                                    plannedAbsenceReason.reason
                        )
                        .map((plannedAbsenceReason, index) => (
                            <div key={index} className="selected-info mt-2">
                                <p className="checked-label">
                                    <i className="Appkit4-icon icon-information-fill" />
                                    {l('_furtherInformation')}
                                </p>
                                {renderFurtherInformation(
                                    plannedAbsenceReason.furtherInformation ??
                                        '',
                                    plannedAbsenceReason.reason ?? undefined
                                )}
                            </div>
                        ))}
                    <div>
                        <div className={'d-flex align-items-center'}>
                            <div className={'me-1'}>
                                <h2 className="ap-typography-heading-s">
                                    {l('_datesApplicable')}
                                </h2>
                            </div>
                            <div>
                                <Tooltip
                                    trigger="hover"
                                    position="top-left"
                                    distance={4}
                                    id="disabled dates tooltip"
                                    appendAfterTarget={true}
                                    content={l('_disabledDatesWarning')}
                                >
                                    <Button
                                        add
                                        icon="icon-information-outline"
                                        compact
                                        kind={'text'}
                                    >
                                        {l('_disabledDatesWarning')}
                                    </Button>
                                </Tooltip>
                            </div>
                        </div>
                        <div className="date-main">{renderDateRanges()}</div>
                        <div className="add-dates-div">
                            <Button
                                kind="secondary"
                                onClick={addNewRow}
                                disabled={
                                    values.dateRanges?.some(
                                        (range) =>
                                            range.startDate == null ||
                                            range.endDate == null
                                    ) ||
                                    (values.dateRanges?.length &&
                                        values?.dateRanges?.length >= 5) ||
                                    hasErrors(errors)
                                }
                            >
                                {l('_addMoreDates')}
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default PlannedSubmissionStepThree;
